// ✅ src/realtime/RealtimeProvider.jsx  (INTEGRADO COMPLETO)
// - WebSocket global (1 sola conexión)
// - Reintento con backoff
// - Bus de eventos (onEvent)
// - Toasts globales + dedupe
// - Contadores: unreadExternal / unreadInternal
// - Helpers WS para internal chat por thread:
//    subscribeInternalThread / subscribeInternalThreads / unsubscribeInternalThreads
// - Soporta:
//    type="new_message" (WhatsApp/cliente)
//    type="internal.notify" (interno dirigido: inbox_changed / thread_created / thread_assigned, etc.)
//    type="internal.message.new" (mensaje interno realtime por thread)
//    type="unread_total" (sincroniza externos)
//    type="internal_unread_total" (sincroniza internos)

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import api, { API_BASE } from "../auth/api";

const RealtimeCtx = createContext(null);

function getToken() {
  try {
    const h = api?.defaults?.headers?.common?.Authorization || "";
    const m = h.match(/^Bearer\s+(.+)$/i);
    return m ? m[1] : localStorage.getItem("token") || "";
  } catch {
    return localStorage.getItem("token") || "";
  }
}

function getCurrentUserIdFromToken() {
  const token = getToken();
  if (!token || !token.includes(".")) return null;
  try {
    let part = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    while (part.length % 4) part += "=";
    const payload = JSON.parse(atob(part));
    return payload?.id ?? payload?.userId ?? null;
  } catch {
    return null;
  }
}

function buildWsUrl(scope = "all") {
  const base = `${API_BASE}`.replace(/\/+$/, "");
  const token = getToken();

  const qp = new URLSearchParams();
  if (token) qp.set("t", token);
  qp.set("scope", scope);

  // IMPORTANTE: debe coincidir con tu upgrade path del backend
  const path = `/ws-chat?${qp.toString()}`;

  if (base.startsWith("https")) return base.replace("https", "wss") + path;
  if (base.startsWith("http")) return base.replace("http", "ws") + path;
  return "ws://localhost:3001" + path;
}

function normalizeIds(ids) {
  const arr = Array.isArray(ids) ? ids : [ids];
  return arr
    .map((x) => Number(x))
    .filter((n) => Number.isFinite(n) && n > 0);
}

function playAlertTone() {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.55);

    const osc = ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.setValueAtTime(1175, ctx.currentTime + 0.18);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.58);
    setTimeout(() => ctx.close().catch(() => {}), 900);
  } catch {}
}

export function RealtimeProvider({ children }) {
  // ===== UI / Contadores =====
  const [toasts, setToasts] = useState([]);
  const [unreadExternal, setUnreadExternal] = useState(0); // WhatsApp / cliente
  const [unreadInternal, setUnreadInternal] = useState(0); // interno

  // ===== WS / Eventos =====
  const wsRef = useRef(null);
  const listenersRef = useRef(new Set()); // fn(evt)
  const reconnectTimerRef = useRef(null);
  const backoffRef = useRef(600);

  // ===== Dedupe =====
  const recentRef = useRef(new Map()); // key -> ts

  // ===== Suscripciones internal (persisten entre reconexiones) =====
  const internalSubsRef = useRef(new Set()); // threadId(number)
  const pendingInternalSubRef = useRef(false);

  function showToast(message) {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, message }]);
    setTimeout(
      () => setToasts((prev) => prev.filter((t) => t.id !== id)),
      3800
    );
  }

  function notifyOnce(key, message) {
    const now = Date.now();
    const last = recentRef.current.get(key) || 0;
    if (now - last < 1500) return;
    recentRef.current.set(key, now);
    showToast(message);
  }

  function emit(evt) {
    for (const fn of listenersRef.current) {
      try {
        fn(evt);
      } catch {}
    }
  }

  function wsSend(obj) {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== 1) return false;
    try {
      ws.send(JSON.stringify(obj));
      return true;
    } catch {
      return false;
    }
  }

  function resubscribeInternalIfNeeded() {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== 1) return;

    const ids = Array.from(internalSubsRef.current);
    if (!ids.length) return;

    // evita spamear si se llama varias veces al reconectar
    if (pendingInternalSubRef.current) return;
    pendingInternalSubRef.current = true;

    wsSend({ type: "internal.subscribe", threadIds: ids });

    // suelta el lock luego de un momento (ack es opcional)
    setTimeout(() => {
      pendingInternalSubRef.current = false;
    }, 800);
  }

  // ==== Helpers públicos para internal chat ====
  function subscribeInternalThreads(threadIds) {
    const ids = normalizeIds(threadIds);
    if (!ids.length) return false;

    ids.forEach((id) => internalSubsRef.current.add(id));

    // si está conectado, manda subscribe ya
    const ok = wsSend({ type: "internal.subscribe", threadIds: ids });
    if (!ok) pendingInternalSubRef.current = false; // se reintentará al reconectar
    return true;
  }

  function unsubscribeInternalThreads(threadIds) {
    const ids = normalizeIds(threadIds);
    if (!ids.length) return false;

    ids.forEach((id) => internalSubsRef.current.delete(id));
    wsSend({ type: "internal.unsubscribe", threadIds: ids });
    return true;
  }

  function subscribeInternalThread(threadId) {
    const id = normalizeIds([threadId])[0];
    if (!id) return false;
    internalSubsRef.current.add(id);
    wsSend({ type: "internal.subscribeOne", threadId: id });
    return true;
  }

  const connect = () => {
    // evita duplicado
    if (wsRef.current) return;

    const ws = new WebSocket(buildWsUrl("all"));
    wsRef.current = ws;

    ws.onopen = () => {
      backoffRef.current = 600;
      emit({ type: "ws.open", payload: {}, ts: Date.now() });

      // re-suscribe internal threads abiertos
      pendingInternalSubRef.current = false;
      resubscribeInternalIfNeeded();
    };

    ws.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        const type = data?.type;
        const payload = data?.payload || {};
        const evt = { type, payload, ts: Date.now() };

        // 0) acks internal (opcional)
        if (type === "internal.subscribed") {
          // payload: { accepted, rejected, joined }
          // No hacemos nada obligatorio, pero puedes usarlo para debug/UX
        }

        // 1) Mensajes externos (WhatsApp/chat)
        if (type === "new_message") {
          const k =
            payload.wa_message_id ||
            payload.id ||
            `${payload.thread_id || ""}|${payload.at || payload.timestamp || ""}|${(
              payload.text || payload.body || ""
            ).slice(0, 40)}`;

          const inbound =
            String(payload.direction || "").toLowerCase() === "in" ||
            payload.from_me === false;

          if (inbound) {
            setUnreadExternal((n) => n + 1);
            if (payload?.event === "flow_order_alert") {
              notifyOnce(`order|${k}`, payload?.text || "Nuevo pedido recibido");
              playAlertTone();
            } else {
              notifyOnce(`ext|${k}`, "Nuevo mensaje de cliente");
            }
          }
        }

        // 2) Internos dirigidos (notificaciones)
        // emitInternalToUser => { type:"internal.notify", payload:{ event, threadId?, thread?, ts } }
        if (type === "internal.notify") {
          const ev = payload?.event || "";
          const threadId = payload?.thread?.id || payload?.threadId || "";
          const ts = payload?.ts || "";
          const currentUserId = getCurrentUserIdFromToken();
          const actorUserId = payload?.actorUserId ?? payload?.actor_user_id ?? null;
          const ownEvent = currentUserId != null && actorUserId != null && String(currentUserId) === String(actorUserId);
          if (ownEvent) {
            emit(evt);
            return;
          }
          notifyOnce(
            `int|${ev}|${threadId}|${ts}`,
            ev === "inbox_changed" ? "Actualización en chat interno" : "Tienes un chat interno nuevo"
          );
          setUnreadInternal((n) => n + 1);
        }

        // 2b) Mensajes internos realtime por thread
        // emitInternalMessageToThread => { type:"internal.message.new", payload:{ threadId, message, ts } }
        if (type === "internal.message.new") {
          const threadId = payload?.threadId || payload?.thread_id || "";
          const msgId = payload?.message?.id || payload?.message_id || "";
          const ts = payload?.ts || "";
          const k = `intmsg|${threadId}|${msgId}|${ts}`;
          const currentUserId = getCurrentUserIdFromToken();
          const messageUserId = payload?.message?.user_id ?? payload?.user_id ?? null;
          const ownMessage =
            currentUserId != null &&
            messageUserId != null &&
            String(currentUserId) === String(messageUserId);

          // Toast suave (dedupe). El contador unreadInternal se recomienda manejarlo
          // en la página si NO está abierto el thread. Aquí solo toast.
          if (!ownMessage) notifyOnce(k, "Nuevo mensaje interno");
        }

        // 3) Sync contadores desde backend (si existe)
        if (type === "unread_total") {
          const total = Number(payload?.total || 0);
          setUnreadExternal(total);
        }
        if (type === "internal_unread_total") {
          const total = Number(payload?.total || 0);
          setUnreadInternal(total);
        }

        // Re-distribuye a las páginas
        emit(evt);
      } catch {}
    };

    ws.onerror = () => {
      try {
        ws.close();
      } catch {}
    };

    ws.onclose = () => {
      wsRef.current = null;
      emit({ type: "ws.close", payload: {}, ts: Date.now() });

      // reintento con backoff
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      const wait = Math.min(backoffRef.current, 10_000);
      backoffRef.current = Math.min(Math.floor(backoffRef.current * 1.6), 10_000);
      reconnectTimerRef.current = setTimeout(() => connect(), wait);
    };
  };

  useEffect(() => {
    connect();
    return () => {
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      try {
        wsRef.current?.close();
      } catch {}
      wsRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo(
    () => ({
      // UI
      toasts,
      toast: showToast,

      // contadores
      unreadExternal,
      unreadInternal,
      clearUnreadExternal: () => setUnreadExternal(0),
      clearUnreadInternal: () => setUnreadInternal(0),

      // bus de eventos
      onEvent: (fn) => {
        listenersRef.current.add(fn);
        return () => listenersRef.current.delete(fn);
      },

      // helper dedupe global
      notifyOnce,

      // WS helpers
      wsSend,

      // INTERNAL helpers
      subscribeInternalThread,
      subscribeInternalThreads,
      unsubscribeInternalThreads,
    }),
    [toasts, unreadExternal, unreadInternal]
  );

  return (
    <RealtimeCtx.Provider value={value}>
      {children}

      {/* Toasts globales */}
      <div className="toast-container">
        {toasts.map((t) => (
          <div key={t.id} className="toast">
            {t.message}
          </div>
        ))}
      </div>

      <style>{`
        .toast-container{ position: fixed; right: 20px; bottom: 20px; display:flex; flex-direction:column; gap:10px; z-index:99999; }
        .toast{
          background: rgba(59,130,246,0.95);
          padding: 14px 18px; color: white; font-weight: 700; border-radius: 10px;
          border: 1px solid rgba(255,255,255,0.15); box-shadow: 0 8px 20px rgba(0,0,0,0.4);
          animation: toast-in 0.35s ease-out, toast-out 0.6s ease-in 3.2s forwards;
          font-size: 14px; min-width: 240px;
        }
        @keyframes toast-in { 0% { transform: translateY(20px) scale(0.95); opacity: 0; } 100% { transform: translateY(0) scale(1); opacity: 1; } }
        @keyframes toast-out { 0% { transform: translateY(0) scale(1); opacity: 1; } 100% { transform: translateY(20px) scale(0.9); opacity: 0; } }
      `}</style>
    </RealtimeCtx.Provider>
  );
}

export function useRealtime() {
  const ctx = useContext(RealtimeCtx);
  if (!ctx) throw new Error("useRealtime must be used within RealtimeProvider");
  return ctx;
}
